/**
 * Collection of easing functions for use with the AnimationController.
 * Add functions to this object as needed.
 * @see {@link https://nicmulvaney.com/easing}
 */
export const Curves = {
  linear: (t: number) => t,
  easeOutCubic: (t: number) => 1 - Math.pow(1 - t, 3),
} as const satisfies Record<string, (t: number) => number>;

/**
 * Controls an animation by updating registered listeners with the eased progress over a specified duration.
 *
 * @remarks
 * The AnimationController class provides a consistent way to perform animations with a custom duration
 * and easing function. Listeners added via {@link addListener} are invoked with the current eased progress
 * (a value between 0 and 1) during each animation frame. The animation is started by calling {@link forward},
 * which returns a Promise that resolves once the animation has completed.
 *
 * The internal state tracks the start time and ensures that multiple calls to {@link forward} do not create
 * overlapping animations.
 *
 * ### Usage Example
 *
 * ```ts
 * const animation = new AnimationController({ durationMs: 500, easingFunction: Curves.easeInOut });
 * animation.addListener((progress) => {
 *   // Apply progress to update UI or perform other actions
 * });
 * await animation.forward();
 * ```
 *
 * @public
 */
export default class AnimationController {
  /** Duration to run the animation for */
  public durationMs: number;
  /** Function describing the curve along which to ease the animation */
  public easingFunction: (t: number) => number;
  private readonly listeners: ((animation: number) => void)[] = [];
  private start: number | null = null;
  private _resultPromise: Promise<void> | null = null;

  /**
   * Gets a boolean value indicating whether an animation is currently in progress.
   *
   * This getter returns `true` if the internal animation result promise is set (indicating an animation is active),
   * or `false` otherwise.
   */
  public get isAnimating(): boolean {
    return !!this._resultPromise;
  }

  constructor({
    durationMs = 300,
    easingFunction = Curves.linear,
  }: Partial<Pick<AnimationController, 'durationMs' | 'easingFunction'>>) {
    this.durationMs = durationMs;
    this.easingFunction = easingFunction;
  }

  /**
   * Registers a listener that will be notified of animation updates.
   *
   * @param listener - A callback function that receives the current animation frame number.
   */
  public addListener(listener: (animation: number) => void): void {
    this.listeners.push(listener);
  }

  /**
   * Clears all registered listeners.
   *
   * This method resets the internal listeners array by setting its length to zero,
   * effectively removing all event callbacks that were previously registered.
   */
  public clearListeners(): void {
    this.listeners.length = 0;
  }

  /**
   * Executes a forward animation by progressively updating registered listeners
   * with the current eased progress until the animation completes.
   *
   * The animation computes the elapsed time relative to a predefined duration (`this.durationMs`)
   * and applies an easing function (`this.easingFunction`) to this progress. It uses `requestAnimationFrame`
   * for smooth, frame-optimized updates. While the animation is in progress, listeners are invoked
   * with the computed eased progress value (`easedT`), and once the progress reaches 100%
   * (i.e., `t` equals 1), the returned promise is resolved.
   *
   * @returns A promise that resolves when the animation has fully completed.
   */
  public forward(): Promise<void> {
    return (this._resultPromise ??= new Promise((resolve) => {
      const play = () => {
        this.start ??= new Date().getTime();
        const now = new Date().getTime();
        const elapsed = now - this.start;
        const t = Math.min(elapsed / this.durationMs, 1);
        const easedT = this.easingFunction(t);

        this.listeners.forEach((listener) => listener(easedT));

        if (t < 1) {
          requestAnimationFrame(play);
        } else {
          this._resultPromise = null;
          resolve();
        }
      };
      play();
    }));
  }
}
